/// Pinia store — System config (SYS-map-provider).
/// Holds the WHOLE client-facing config map (loaded once at first-load) + the
/// management entries list (Super Admin). New client-facing settings appear
/// automatically via `value(key)` — no store change needed.
/// `error` = load error (replaces form); `actionError` = save error (kept inline).
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { apiErrorMessage as formatError } from '@/core/api/api-error'
import { systemSettingsApi } from '@/features/settings/api/system-settings-api'
import {
  GOOGLE_MAPS_API_KEY_WEB,
  MAP_PROVIDER_SETTING,
} from '@/features/settings/constants/system-setting-fields'
import type { GoogleMapsKeyReport, SystemSettingResponse } from '@/types/api'

/** Canonical DB values → code palette used by the map layer. */
export type MapProvider = 'google' | 'osm'

function normalizeProvider(value: string | null | undefined): MapProvider {
  if (value === 'google_map') return 'google'
  // 'openstreetmap' or missing/unknown (hydrate error, setting absent) → OSM (fail-safe, free).
  return 'osm'
}

export const useSystemConfigStore = defineStore('system-config', () => {
  /** Effective client-facing config (setting_key → value); loaded once at app start. */
  const config = ref<Record<string, string | null>>({})
  /** Management list (Super Admin screen). */
  const entries = ref<SystemSettingResponse[]>([])
  const hydrated = ref(false)
  const isLoading = ref(false)
  const isSubmitting = ref(false)
  const error = ref<string | null>(null)
  const actionError = ref<string | null>(null)
  /** Last Google check per setting_key — advisory, never gates a save. */
  const keyReports = ref<Record<string, GoogleMapsKeyReport>>({})
  /** setting_key currently being checked (one at a time), else null. */
  const checkingKey = ref<string | null>(null)

  /** Map provider for the whole Web session ('google' | 'osm'). Fallback 'osm' (free/safe). */
  const mapProvider = computed<MapProvider>(() => normalizeProvider(config.value[MAP_PROVIDER_SETTING]))

  /**
   * Google Maps JS API key ('' when unset). Empty → the map layer skips Google and uses
   * OpenStreetMap, so a missing key degrades instead of breaking the screen.
   */
  const googleMapsApiKey = computed<string>(() =>
    (config.value[GOOGLE_MAPS_API_KEY_WEB] ?? '').trim(),
  )

  /** Read any client-facing setting value by key (for future settings). */
  function value(key: string): string | null {
    return config.value[key] ?? null
  }

  /**
   * Load client-facing config once at first-load. Best-effort: on error → mapProvider
   * falls back to 'osm'. Call-coalescing: concurrent callers (App.vue + MapTrackingPage)
   * share one in-flight request; returns immediately once hydrated.
   */
  let inflight: Promise<void> | null = null
  async function hydrate(): Promise<void> {
    if (hydrated.value) return
    if (inflight) return inflight
    inflight = (async () => {
      try {
        const res = await systemSettingsApi.clientConfig()
        config.value = res.settings ?? {}
      } catch {
        config.value = {}
      } finally {
        hydrated.value = true
        inflight = null
      }
    })()
    return inflight
  }

  /** Super Admin: load full settings list. */
  async function fetchAll(target?: string): Promise<void> {
    isLoading.value = true
    error.value = null
    try {
      entries.value = await systemSettingsApi.list(target)
    } catch (err) {
      error.value = formatError(err)
    } finally {
      isLoading.value = false
    }
  }

  /** Super Admin: update one setting; keeps entries + config in sync. */
  async function update(key: string, newValue: string | null): Promise<boolean> {
    isSubmitting.value = true
    actionError.value = null
    try {
      const updated = await systemSettingsApi.update(key, newValue)
      entries.value = entries.value.map((e) => (e.settingKey === key ? updated : e))
      config.value = { ...config.value, [key]: updated.settingValue ?? updated.defaultValue }
      return true
    } catch (err) {
      actionError.value = formatError(err)
      return false
    } finally {
      isSubmitting.value = false
    }
  }

  /**
   * Super Admin: ask Google about a Google Maps key (advisory).
   * Stores the report under `settingKey`. On failure it sets `actionError` AND drops any
   * previous report: leaving the old panel up would let a failed check read as a clean
   * bill of health for a value it never saw.
   */
  async function checkGoogleMapsKey(settingKey: string, value: string): Promise<boolean> {
    if (settingKey !== GOOGLE_MAPS_API_KEY_WEB) return false
    checkingKey.value = settingKey
    actionError.value = null
    try {
      keyReports.value = {
        ...keyReports.value,
        [settingKey]: await systemSettingsApi.validateGoogleMapsKey(value),
      }
      return true
    } catch (err) {
      actionError.value = formatError(err)
      clearKeyReport(settingKey)
      return false
    } finally {
      checkingKey.value = null
    }
  }

  /** Drop a stale report once the field is edited — it no longer describes what is typed. */
  function clearKeyReport(settingKey: string): void {
    if (!keyReports.value[settingKey]) return
    const next = { ...keyReports.value }
    delete next[settingKey]
    keyReports.value = next
  }

  /** Super Admin: reset one setting to its default value. */
  async function resetToDefault(key: string): Promise<boolean> {
    const entry = entries.value.find((e) => e.settingKey === key)
    return update(key, entry?.defaultValue ?? null)
  }

  return {
    config,
    entries,
    hydrated,
    isLoading,
    isSubmitting,
    error,
    actionError,
    keyReports,
    checkingKey,
    mapProvider,
    googleMapsApiKey,
    value,
    hydrate,
    fetchAll,
    update,
    resetToDefault,
    checkGoogleMapsKey,
    clearKeyReport,
  }
})
